import { Link } from "react-router-dom";
import { ArrowRight, BadgeCheck, Factory, FileCheck, FlaskConical, GraduationCap, HeartHandshake, Microscope, Quote, Scale, Wrench } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";
import PageHero from "@/components/PageHero";
import { Counter, FadeUp, SectionHeading } from "@/components/Reveal";
import { FACILITY } from "@/lib/images";
import { SITE } from "@/lib/site";

const CAPABILITIES = [
  { icon: Factory, title: "Manufacture & Supply", copy: "Laboratory, research, educational and medical instruments manufactured under the 'Krithish' brand and supplied across the board." },
  { icon: Wrench, title: "Repairing & Servicing", copy: "All scientific, industrial & lab equipment repairing & servicing undertaken — electrical, electronic & cooling equipment." },
  { icon: FlaskConical, title: "Chemical & Glass Wares", copy: "All types of laboratory glassware and chemicals supplied alongside your equipment order." },
  { icon: Scale, title: "Balances & Weighing", copy: "Digital balances and platform weighing balances of all types for lab and industrial use." },
  { icon: Microscope, title: "Microscopes & Meters", copy: "Lab microscopes of all types, digital pH meters and digital moisture meters." },
  { icon: GraduationCap, title: "Educational & Medical", copy: "Complete lab setups for schools, colleges, hospitals and research institutions." },
];

const CERTS = [
  { icon: FileCheck, title: "GST Registered", copy: "GSTIN 33AINPR0146B1ZM — registered for institutional and government procurement." },
  { icon: BadgeCheck, title: "'Krithish' Branded Manufacture", copy: "Mfrs. of 'Krithish' Scientific, Industrial & Lab Equipments — our name on every unit." },
  { icon: HeartHandshake, title: "Customer-First Service", copy: "An excellent track record for the best customer satisfaction — in constant touch before and after the sale." },
];

export default function About() {
  usePageMeta(
    "About Us | Krithish Scientific Company",
    "Krithish Scientific Company, Chennai — 15+ years manufacturing and supplying laboratory, research, educational and medical instruments. Founder R. Anbazhagan. Repairing & servicing of all lab equipment undertaken."
  );

  return (
    <>
      <PageHero
        eyebrow="About Krithish Scientific Company"
        title="The maker behind 'Krithish' lab equipments."
        sub="Fifteen-plus years of manufacturing, supplying and servicing laboratory, research, educational and medical instruments from Chennai."
      />

      {/* Story */}
      <section className="bg-white py-24 lg:py-32" data-testid="about-story">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid items-center gap-14 lg:grid-cols-2">
            <FadeUp>
              <div className="relative overflow-hidden rounded-2xl">
                <img src={FACILITY.fab} alt="Equipment fabrication at the Krithish Scientific workshop" loading="lazy" className="aspect-[4/3] w-full object-cover" />
                <span className="absolute bottom-4 left-4 rounded bg-navy/85 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-rose-300">
                  Chennai Workshop
                </span>
              </div>
            </FadeUp>
            <FadeUp delay={0.1}>
              <SectionHeading
                eyebrow="Our Story"
                title="A supplier labs have trusted for over 15 years."
              />
              <div className="mt-6 space-y-4 text-base leading-relaxed text-slate-600">
                <p>
                  Krithish Scientific Company has been in the business, manufacture and supply of{" "}
                  <span className="font-semibold text-slate-900">laboratory, research, educational and medical
                  instruments</span> for the past 15+ years — an established and popular company with an excellent
                  track record for the best customer satisfaction.
                </p>
                <p>
                  We have never compromised on the quality and the services provided to the customer. We believe in
                  keeping customers happy and providing products at a very competent price, with staff who guide you
                  by keeping in constant touch and informing you about the market.
                </p>
                <p>
                  Beyond manufacturing, we undertake <span className="font-semibold text-slate-900">repairing &amp;
                  servicing of all scientific, industrial and lab equipments</span> — electrical, electronic &amp;
                  cooling — and supply chemicals &amp; glass wares. From our base at M.G.R. Nagar, Chennai, we serve
                  institutions across the region.
                </p>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* Founder */}
      <section className="bg-slate-50 py-24 lg:py-32" data-testid="about-founder">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid gap-10 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <SectionHeading eyebrow="Leadership" title="A founder who stays reachable." sub="Deal directly with the maker — from first enquiry to after-sales service." />
            </div>
            <div className="lg:col-span-7">
              <FadeUp delay={0.1}>
                <div className="flex h-full flex-col justify-between rounded-2xl border border-slate-200 bg-white p-8 shadow-sm lg:p-10">
                  <Quote size={36} className="text-rose-200" />
                  <p className="mt-6 font-display text-xl leading-relaxed text-slate-800 sm:text-2xl">
                    "We have never compromised on the quality and the services provided to the customer. We believe in
                    keeping the customers happy and providing them with products at a very competent price."
                  </p>
                  <div className="mt-8 flex items-center gap-4">
                    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-navy font-display text-lg font-extrabold text-rose-400">
                      RA
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">R. Anbazhagan</p>
                      <p className="text-sm text-slate-500">Founder · Krithish Scientific Company</p>
                    </div>
                  </div>
                </div>
              </FadeUp>
            </div>
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="bg-white py-24 lg:py-32" data-testid="about-capabilities">
        <div className="mx-auto max-w-7xl px-6">
          <SectionHeading
            eyebrow="Manufacturing Capabilities"
            title="Manufacture, supply and service — under one roof."
            sub="From our Chennai workshop we build, sell and keep running everything a working laboratory needs."
          />
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {CAPABILITIES.map((c, i) => (
              <FadeUp key={c.title} delay={i * 0.05} className="h-full">
                <div className="h-full rounded-2xl border border-slate-200 p-6 transition hover:border-rose-200 hover:shadow-lg hover:shadow-slate-200/50">
                  <div className="flex items-center justify-between">
                    <span className="inline-flex rounded-lg bg-rose-50 p-2.5 text-safety">
                      <c.icon size={20} />
                    </span>
                    <span className="font-mono text-xs text-slate-300">{String(i + 1).padStart(2, "0")}</span>
                  </div>
                  <h3 className="mt-4 font-display text-lg font-bold tracking-tight text-slate-900">{c.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{c.copy}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Credentials + stats */}
      <section className="relative overflow-hidden bg-navy py-24 lg:py-28" data-testid="about-certifications">
        <div className="absolute inset-0 bg-grid-dark" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-6">
          <div className="grid gap-10 lg:grid-cols-3">
            {CERTS.map((c, i) => (
              <FadeUp key={c.title} delay={i * 0.08}>
                <div className="rounded-2xl border border-white/10 bg-white/5 p-8 backdrop-blur">
                  <c.icon size={28} className="text-rose-400" />
                  <h3 className="mt-4 font-display text-xl font-bold text-white">{c.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-white/60">{c.copy}</p>
                </div>
              </FadeUp>
            ))}
          </div>
          <div className="mt-16 grid grid-cols-2 gap-8 border-t border-white/10 pt-12 sm:grid-cols-4">
            {[
              { to: 15, suffix: "+", label: "Years of Experience" },
              { to: 40, suffix: "+", label: "Product Types" },
              { to: 8, suffix: "", label: "Core Equipment Lines" },
            ].map((s, i) => (
              <FadeUp key={s.label} delay={i * 0.06}>
                <p className="font-display text-3xl font-extrabold text-rose-400 sm:text-4xl">
                  <Counter to={s.to} suffix={s.suffix} />
                </p>
                <p className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">{s.label}</p>
              </FadeUp>
            ))}
            <FadeUp delay={0.18}>
              <p className="font-display text-3xl font-extrabold text-rose-400 sm:text-4xl">Chennai</p>
              <p className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">M.G.R. Nagar Workshop</p>
            </FadeUp>
          </div>
          <FadeUp delay={0.2}>
            <div className="mt-14 flex flex-wrap items-center gap-4">
              <Link to="/products" className="inline-flex items-center gap-2 rounded-lg bg-safety px-6 py-3.5 text-sm font-semibold text-white transition hover:bg-safety-dark">
                Browse our catalog <ArrowRight size={16} />
              </Link>
              <a href={SITE.phoneHref} className="inline-flex items-center gap-2 rounded-lg border border-white/40 px-6 py-3.5 font-mono text-sm font-semibold text-white transition hover:bg-white hover:text-navy">
                {SITE.phoneDisplay}
              </a>
            </div>
          </FadeUp>
        </div>
      </section>
    </>
  );
}
